import {
  Box,
  Divider,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  useTheme,
} from "@mui/material";
import * as React from "react";

import { useHttpClient } from "@/providers/HttpClient";
import {
  RobotoTableCell,
  RobotoTableRow,
  RobotoTypography,
} from "@/shared/components";
import { FormattedDateTime } from "@/shared/datetime";
import { MigrationRecord } from "@/shared/domain/admin/MigrationRecord";
import { LoggerService } from "@/shared/services/LoggerService";

/**
 * Displays a table of database migrations
 */
export function Migrations() {
  const theme = useTheme();
  const { httpClient } = useHttpClient();
  const [isLoading, setIsLoading] = React.useState<boolean>(true);
  const [migrations, setMigrations] = React.useState<MigrationRecord[]>([]);

  React.useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const url = httpClient.constructUrl("v1/admin/migrations");
        const response = await httpClient.get(url);
        const data = await response.json<MigrationRecord[]>();
        setMigrations(data);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData().catch((err) => {
      LoggerService.error("Failed to fetch migrations", err);
    });
  }, [httpClient]);

  // Sort migrations by created_at in descending order
  const sortedMigrations = React.useMemo(() => {
    return [...migrations].sort((a, b) => {
      return (
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );
    });
  }, [migrations]);

  return (
    <Box>
      <RobotoTypography variant="body1" sx={{ marginBottom: theme.spacing(2) }}>
        Database Migrations
      </RobotoTypography>
      <Divider sx={{ marginBottom: theme.spacing(2) }} />
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <RobotoTableCell>Filename</RobotoTableCell>
              <RobotoTableCell>Created At</RobotoTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <>
                {Array.from({ length: 5 }, (_, i) => (
                  <TableRow key={`row-${i}`}>
                    {Array.from({ length: 2 }, (_, j) => (
                      <RobotoTableCell key={`cell-${j}`}>
                        <Skeleton
                          animation="wave"
                          variant="rounded"
                          height={30}
                          sx={{ mt: 1, mb: 1 }}
                        />
                      </RobotoTableCell>
                    ))}
                  </TableRow>
                ))}
              </>
            ) : (
              <>
                {sortedMigrations.length > 0 ? (
                  <>
                    {sortedMigrations.map((migration) => (
                      <RobotoTableRow key={migration.filename}>
                        <RobotoTableCell>{migration.filename}</RobotoTableCell>
                        <RobotoTableCell>
                          <FormattedDateTime value={migration.created_at} />
                        </RobotoTableCell>
                      </RobotoTableRow>
                    ))}
                  </>
                ) : (
                  <RobotoTableRow>
                    <RobotoTableCell colSpan={2}>
                      No migrations found
                    </RobotoTableCell>
                  </RobotoTableRow>
                )}
              </>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
